import axios from "axios";
import useAxios, {configure} from "axios-hooks";
import {toast} from "react-toastify";
import {apiUrl} from "../config.js";
import routes from "../consts/routes.json";
import {getCurrentUser, getJwt, logout} from "./JwtService";
import {logger} from "./LogService";

function init() {
    const apiToReturn = axios.create({
        baseURL: apiUrl,
    });

    apiToReturn.interceptors.response.use(null, (error) => {
        const status = error.response && error.response.status;

        if (error.response?.data?.error) {
            toast.error(error.response.data.error);
        }

        if (error.response?.data?.uuid) {
            logger.error("Trace-Code: " + error.response?.data?.uuid);
        }

        if (status && status > 400 && status < 500) {
            if (status === 401) {
                toast.error("Sie sind nicht berechtigt diese Seite zu öffnen.");
                return handle401(error);
            } else if (status === 403) {
                return handle403();
            } else if (status === 404) {
                return handle404();
            } else {
                logger.debug(error);
            }
        }
        if (status && status === 500) {
            logger.error(error);
        }

        if (error.code === "ERR_CANCELED") {
            return Promise.resolve({status: 499})
        }

        return Promise.reject(error);
    });
    configure({axios: apiToReturn, cache: false});
    return apiToReturn;
}

function handle401(error) {
    if (!getCurrentUser()) {
        return Promise.reject(error);
    } else {
        logout();
        window.location = process.env.PUBLIC_URL;
        return resolveGlobalError();
    }
}

function handle403() {
    const user = getCurrentUser();
    if (user && Date.now() >= user.exp * 1000) {
        logout();
        window.location = process.env.PUBLIC_URL;
        toast.error("Sie sind nicht berechtigt diese Seite zu öffnen.");
        return resolveGlobalError();
    }
    window.history.go(-1);
    toast.error("Sie sind nicht berechtigt diese Seite zu öffnen.");
    return resolveGlobalError();
}

function handle404() {
    window.location = process.env.PUBLIC_URL + routes.NICHT_GEFUNDEN;
    toast.error("Die gewünschte Seite existiert nicht.");
    return resolveGlobalError();
}

function resolveGlobalError() {
    return Promise.resolve({
        then: function () {
        },
    });
}

/**
 * Hook, mit welchem ein get-Endpoint angesprochen werden kann.
 *
 * Gibt ein Objekt mit den Feldern
 * <ul>
 *     <li>data - The success response data property (for convenient access).</li>
 *     <li>loading - True if the request is in progress, otherwise False.</li>
 *     <li>error - The error value</li>
 *     <li>response - The whole success response object.</li>
 *  </ul>
 *  sowie eine Funktion zum erneuten Abrufen des get-Endpoints zurück.
 *  Benutzung: const [{ data, loading, error, response }, reFetch] = useHttp("Url")
 * @param config die Url oder Konfiguration für die Kommunikation
 * @param options optionales Objekt mit den Werten(manual: false, useCache: false)
 * @returns
 */
export const useHttp = (config, options) => {
    return useAxios(config, options);
};

/**
 * Funktion, welche prüft, ob eine REST-Abfrage abgebrochen wurde, da beispielsweise weg navigiert wurde.
 * @param exception
 * @returns {boolean}
 */
export const isApiCancel = (exception) => {
    return axios.isCancel(exception);
};

/**
 * Prüft mit isApiCancel, ob die Exception durch einen Abbruch einer Abfrage geworfen wurde. In dem Fall wird nichts gemacht.
 * Ansonsten wird der Fehler geloggt und eine entsprechende Meldung an den Nutzer ausgegeben.
 * @param exception
 * @param logText
 * @param toastMessage
 */
export const handleApiException = (exception, logText, toastMessage = "Ein unbekannter Fehler ist aufgetreten.") => {
    let errorText = exception?.response?.data?.error;

    if (!isApiCancel(exception)) {
        logger.error(logText, exception);
        toast.error(toastMessage);
        if (logText) toast.error(logText);
        if (errorText) toast.error(errorText);
    }
};

export const handleValidatedApiException = (exception, logText, toastMessage) => {
    if (exception.isValidationHandled) {
        return;
    }
    handleApiException(exception, logText, toastMessage);
};

const api = init();
setJwt();

function setJwt() {
    if (getJwt()) {
        api.defaults.headers.common["Authorization"] = "Bearer " + getJwt();
    }
}

export function unsetJwt() {
    api.defaults.headers.common["Authorization"] = null;
}

const methods = {
    get: api.get,
    post: api.post,
    put: api.put,
    patch: api.patch,
    delete: api.delete,
    setJwt,
};

export default methods;
